import { useContext, useEffect, useState } from "react";
import { checkAppVersion } from '../../../utils/checkAppVersion';
import { Col, Form, Row } from "react-bootstrap";
import logoSmall from "../../../assets/custom/classic/logo_login_small.svg";
import { ICountry, IForgotPasswordParams, IForgotPasswordRequest, IForgotState, ILogin, loginSchema } from "../../../utils/types";
import LoginUsernameInput from "./components/LoginUsernameInput";
import LoginPasswordInput from "./components/LoginPasswordInput";
import LoginButton from "./components/LoginButton";
import LoginTerm from "./components/LoginTerm";
import Loading from "../../components/Loading";
import AlertErro from "../../components/AlertErro";
import { UiContext } from "../../../context/UiContext";

import { useAuth } from "../../../context/useAuth";
import { useTranslation } from "react-i18next";
import "../stylePage.css";
import Home from "../Home";
import * as BsIcons from "react-icons/bs";
import urlConf from "../../../utils/urlTem";
import {
  geti18nextLngLocalStorage,
  getlangStorage,
} from "../../../context/utils";
import { UserPreferenceContext } from "../../../context/UserPreferenceContext";
import {
  getUrlTerm,
  handleChangeAux,
  handleForgotPassword,
  loadDefinitionsAuthStorage,
} from "./LoginUtils";
import i18next from "i18next";
import ForgotPasswordForm from "./ForgotPasswordForm";
import { useLocation, useNavigate } from "react-router-dom";

const styleForLink = {
  textDecoration: "none",
  color: "#0032ff",
  borderBottom: "1px solid #0032ff",
  cursor: "pointer",
};

const Login = ({ countries }: { countries: ICountry[] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { userAuthenticated, userMenssageApi } = useContext(
    UserPreferenceContext
  );

  const [user, setUser] = useState<ILogin>(() => {
    const keepConnectedStorage = localStorage.getItem("keepConnected");
    const termsStorage = localStorage.getItem("terms");

    let savedKeepConnected = false;
    let savedTerms = false;

    if (keepConnectedStorage) {
      savedKeepConnected = JSON.parse(keepConnectedStorage);
    }

    if (savedKeepConnected && termsStorage) {
      savedTerms = JSON.parse(termsStorage);
    }

    return {
      username: "",
      password: "",
      keepConnected: savedKeepConnected,
      terms: savedTerms,
      countryId: 1,
      language: i18next?.language ?? geti18nextLngLocalStorage() ?? "pt-BR",
    } as ILogin
  });

  const [errorLogin, setErrorLogin] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const auth = useAuth();

  const isLoading = loading || (auth.isAutoLoginLoading ?? false);

  const [urlTerm, setUrlTerm] = useState<string>("");
  const [clickedForgot, setClickedForgot] = useState<boolean>(false);

  const [loadingForgot, setLoadingForgot] = useState<boolean>(false);

  const { isMobile } = useContext(UiContext);
  

  useEffect(() => {
    localStorage.setItem("keepConnected", JSON.stringify(user.keepConnected));

    if (user.keepConnected) {
      localStorage.setItem("terms", JSON.stringify(user.terms));
      localStorage.setItem("rememberLogin", "true");
    } else {
      localStorage.removeItem("terms");
      localStorage.removeItem("rememberLogin");
      localStorage.removeItem("UserAuth");
    }
  }, [user.keepConnected]);

  useEffect(() => {
    if (user.keepConnected) {
      localStorage.setItem("terms", JSON.stringify(user.terms));
    }
  }, [user.terms, user.keepConnected]);

  useEffect(() => {
    handleLoadChangePassword();
  });

  useEffect(() => {
    loadUrlTerm();
  }, [countries]);


  useEffect(() => {
    if (countries && countries.length > 0) {

      const selectElement = document.getElementById('fieldCountryId') as HTMLSelectElement;
      if (selectElement && selectElement.value) {
        const currentCountryId = Number(selectElement.value);

        if (currentCountryId !== user.countryId) {
          setUser(prev => ({ ...prev, countryId: currentCountryId }));
        }
      } else if (countries.length > 0) {

        const firstCountryId = countries[0].CountryID;
        if (firstCountryId !== user.countryId) {
          setUser(prev => ({ ...prev, countryId: firstCountryId }));
        }
      }
    }
  }, [countries, user.countryId]);

  useEffect(() => {
    loadLogin();
  }, [userAuthenticated, userMenssageApi]);

  async function loadLogin() {
    loadDefinitionsAuthStorage(setUser);
    handleChangeLanguage("language", getlangStorage());
    if (userMenssageApi) {
      setErrorLogin(userMenssageApi);
    }
  }
  async function handleLogin() {
    setErrorLogin("");
    setLoading(true);

    await checkAppVersion();

    try {
      const value = await loginSchema.validate(user);
      if (value) {
        await handleAuth();
      }
    } catch (err) {
      console.log(JSON.stringify(err));
      if (err instanceof Error) {
        if (err.message.includes("password")) {
          setErrorLogin(t("password.maxCharacters"));
        } else {
          setErrorLogin(t("login.maxCharacters"));
        }
      }
    } finally {
      setLoading(false);
    }
  }
  async function handleAuth() {
    const response = await auth.authenticate(
      user.username,
      user.password,
      user.countryId,
      user.language ?? geti18nextLngLocalStorage() ?? "pt-BR",
      user.keepConnected ?? false,
      user.terms ?? false
    );
    const resposeResult = await response;
    let resposeResultText: string = resposeResult.toString();
    if (await (!response || response.error)) {
      if (response.error == "password_expired") {
        navigate("/changepassword", { state: { error: response.error_description, username: user.username, countryId: user.countryId } });
      } else {
        setErrorLogin(response.error_description)
      }

    } else if (
      resposeResult.error?.status === 500 ||
      resposeResultText.indexOf("One or more errors occurred.") >= 0
    ) {
      setErrorLogin(t("login.errorApi"));
    } else {
      navigate("/");
    }
  }

  if (userAuthenticated === true) return <Home />;

  if (!countries || countries.length === 0) return <Loading show={true} isMobile={isMobile} />;

  async function loadUrlTerm() {
    if (countries && countries.length > 0) {
      let urlTerm = getUrlTerm(user, countries, urlConf, handleChange);
      setUrlTerm(urlTerm);
      if (user.countryId === 0) {
        let newUser = { ...user };
        newUser.countryId = countries[0].CountryID;
        setUser(newUser);
      }
    } else if (user.countryId === 0) {
      let newUser = { ...user };
      newUser.countryId = 1;
      setUser(newUser);
    }
  }
  async function handleChangeLanguage(field: string, value: any) {
    handleChangeAux(field, value, getlangStorage, setUser, i18n, setErrorLogin);
  }
  async function handleChangeCountry(field: string, value: any) {
    if (value) {
      setUser((prev) => ({ ...prev, [field]: Number(value) }));
      await loadUrlTerm();
    }
  }
  async function handleChange(field: string, value: any) {
    setUser((prev) => ({ ...prev, [field]: value }));
  }
  async function handleLoadChangePassword() {
    if (loadingForgot === false) {
      const paramsGet: IForgotPasswordParams[] = [
        { key: "token", value: null },
        { key: "language", value: null },
      ];
      const restfp: IForgotPasswordRequest = {
        location,
        navigate,
        searchParam: "path",
        pathToCheck: "forgotPassword",
        pathToRedirect: "/changepassword",
        paramsGet,
      };
      handleForgotPassword(restfp);
    }
    setLoadingForgot(true);
  }

  if (clickedForgot) {
    const forgotState: IForgotState = {
      clickedForgot,
      setClickedForgot,
    };
    return (
      <ForgotPasswordForm countries={countries} forgotState={forgotState} />
    );
  }

  return (
    <div className="login-container login-page">
      <div className="login-form-wrapper">
        <div className="login-form-card">
          {/* Logo e Header */}
          <div className="login-form-header">
            <img src={logoSmall} alt="IBOPE" className="login-logo" />
            <h1 className="login-title" style={{ color: "#001e78" }}>Realtime login</h1>
          </div>

          <form className="login-form">
            {/* Campos de input */}
            <div className="login-input-group">
              <LoginUsernameInput
                login={user}
                handleChange={handleChange}
                placeholder={t("login.username")}
              />
            </div>

            <div className="login-input-group">
              <LoginPasswordInput
                login={user}
                handleChange={handleChange}
                placeholder={t("login.password")}
              />
            </div>

            {/* Country Selector e Forgot Password na mesma linha */}
            <div className="login-country-forgot-row">
              <div className="login-country-wrapper">
                <Form.Select
                  id="fieldCountryId"
                  className="login-select"
                  onChange={(e) => {
                    handleChangeCountry("countryId", e.target.value);
                  }}
                  value={user.countryId}
                >
                  {countries.map((option) => (
                    <option key={option.CountryID} value={option.CountryID}>
                      {option.CountryDesc}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="login-forgot-password-inline">
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => setClickedForgot(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setClickedForgot(true);
                    }
                  }}
                >
                  {t("password.forgotPasswordLink")}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="login-remember-me">
              <div className="remember-me-wrapper" onClick={() => handleChange("keepConnected", !user.keepConnected)}>
                <BsIcons.BsCheck
                  className={
                    user.keepConnected === false
                      ? "no-checked-input"
                      : "checked-input"
                  }
                />
                <label className="remember-me-label">
                  {t("login.saveInfo")}
                </label>
              </div>
            </div>

            {/* Erro */}
            {errorLogin && (
              <div className="login-error">
                <AlertErro text={errorLogin} setShow={setErrorLogin} />
              </div>
            )}

            {/* Terms of Use */}
            <div className="login-terms">
              <div className="terms-wrapper" onClick={() => handleChange("terms", !user.terms)}>
                <BsIcons.BsCheck
                  className={
                    user.terms === false ? "no-checked-input" : "checked-input"
                  }
                />
                <LoginTerm
                  handleChange={handleChange}
                  login={user}
                  urlTerm={urlTerm}
                  styleForLink={styleForLink}
                  textReadAndAgree={t("login.iAgree")}
                  textTerm={t("login.tos")}
                />
              </div>
            </div>

            {/* Botão Login */}
            <LoginButton
              login={user}
              loading={isLoading}
              handleClick={handleLogin}
              t={t}
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
